import Portal from '@/components/common/Portal';
/**
 * Enhanced Task Panel
 * ====================
 * Provides detailed task analysis with AI insights, pattern recognition,
 * impact assessment, and direct links to relevant dashboard areas.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  X, ChevronRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Clock, DollarSign, Target, BarChart3, Activity, Lightbulb, History,
  ArrowRight, Sparkles, Shield, Users, FileText, Calendar, ExternalLink,
  ThumbsUp, ThumbsDown, Repeat, Brain, Eye, Zap
} from 'lucide-react';

// Task category to navigation mapping — path + focus deep-link to the exact
// section on arrival (consumed by useSectionFocus via ?focus=<key>).
const TASK_NAVIGATION = {
  financial: { path: '/app/management', focus: 'financials', section: 'P&L Analysis', icon: DollarSign },
  operations: { path: '/app/management', focus: 'insights', section: 'Operations', icon: Activity },
  hr: { path: '/app/team', focus: 'team', section: 'Staff', icon: Users },
  service: { path: '/app/management', focus: 'insights', section: 'Customer Feedback', icon: ThumbsUp },
  procurement: { path: '/app/management', focus: 'suppliers', section: 'Suppliers', icon: FileText },
  inventory: { path: '/app/management', focus: 'suppliers', section: 'Inventory', icon: BarChart3 },
  compliance: { path: '/app/management', focus: 'insights', section: 'Compliance', icon: Shield },
  marketing: { path: '/app/marketing-intelligence', focus: 'marketing', section: 'Marketing', icon: Target },
  product: { path: '/app/market-intelligence', focus: 'product', section: 'Product', icon: Lightbulb },
  growth: { path: '/app/market-intelligence', focus: 'growth', section: 'Growth Metrics', icon: TrendingUp },
};

// Mock AI insights for tasks - would come from backend in production
const getTaskInsights = (task) => {
  const insights = {
    'Review weekly P&L report': {
      impact: { revenue: '+$2,400/week', margin: '+1.2%', rating: 'high' },
      patterns: [
        { type: 'recurring', message: 'This task occurs weekly and has been completed on time 87% of the time', trend: 'positive' },
        { type: 'correlation', message: 'Weeks with timely P&L review show 15% better cost control', trend: 'positive' },
        { type: 'warning', message: 'Last 2 weeks had delayed reviews - margins dropped 0.8%', trend: 'negative' }
      ],
      aiRecommendation: 'Schedule P&L review for Monday mornings to catch weekend variances early. Consider setting up automated alerts for margins below 62%.',
      improvements: [
        { metric: 'Food Cost', current: '31.2%', target: '30%', trend: 'improving', change: '-0.5%' },
        { metric: 'Labor Cost', current: '28.5%', target: '28%', trend: 'stable', change: '0%' },
        { metric: 'EBITDA', current: '18.3%', target: '20%', trend: 'improving', change: '+0.8%' }
      ],
      concerns: [
        { area: 'Beverage COGS', issue: 'Up 12% vs last month', action: 'Review pricing/wastage' },
        { area: 'Overtime Hours', issue: '35% above forecast', action: 'Adjust rostering' }
      ],
      historicalCompletion: 87,
      avgCompletionTime: '23 mins',
      lastCompleted: '7 days ago'
    },
    'Approve new menu pricing': {
      impact: { revenue: '+$8,500/month', margin: '+2.1%', rating: 'high' },
      patterns: [
        { type: 'recurring', message: 'Menu pricing typically reviewed quarterly', trend: 'positive' },
        { type: 'correlation', message: 'Last price increase led to 3% revenue boost with only 1.2% volume drop', trend: 'positive' },
        { type: 'market', message: 'Competitor pricing increased 8% in past 6 months', trend: 'neutral' }
      ],
      aiRecommendation: 'Current food costs suggest 5-7% price increase is justified. Focus on high-margin items first. Consider bundling strategy for premium dishes.',
      improvements: [
        { metric: 'Avg Check', current: '$78', target: '$85', trend: 'improving', change: '+$3' },
        { metric: 'Item Margin', current: '64%', target: '68%', trend: 'stable', change: '0%' }
      ],
      concerns: [
        { area: 'Price Sensitivity', issue: 'Lunch menu may be price-capped', action: 'Test with specials first' }
      ],
      historicalCompletion: 92,
      avgCompletionTime: '45 mins',
      lastCompleted: '3 months ago'
    },
    'Staff performance reviews': {
      impact: { revenue: '+$3,200/month', margin: '+0.8%', rating: 'medium' },
      patterns: [
        { type: 'recurring', message: 'Quarterly review cycle - next due in 2 weeks', trend: 'neutral' },
        { type: 'correlation', message: 'Teams with regular reviews show 22% higher productivity', trend: 'positive' },
        { type: 'warning', message: '3 staff members havent had reviews in 6+ months', trend: 'negative' }
      ],
      aiRecommendation: 'Prioritize reviews for kitchen team - turnover risk identified for 2 members. Consider implementing monthly 1-on-1s to reduce formal review burden.',
      improvements: [
        { metric: 'Team Productivity', current: '94%', target: '95%', trend: 'improving', change: '+2%' },
        { metric: 'Staff Retention', current: '78%', target: '85%', trend: 'declining', change: '-3%' }
      ],
      concerns: [
        { area: 'Turnover Risk', issue: '2 key staff at risk of leaving', action: 'Schedule retention discussions' }
      ],
      historicalCompletion: 75,
      avgCompletionTime: '2 hours',
      lastCompleted: '3 months ago'
    },
    'Review customer feedback trends': {
      impact: { revenue: '+$1,800/month', margin: '+0.4%', rating: 'medium' },
      patterns: [
        { type: 'recurring', message: 'Weekly review recommended based on feedback volume', trend: 'neutral' },
        { type: 'correlation', message: 'Responding within 24hrs increases repeat visits by 18%', trend: 'positive' },
        { type: 'positive', message: 'Overall sentiment improved 12% this month', trend: 'positive' }
      ],
      aiRecommendation: 'Focus on service speed mentions - appearing in 34% of recent reviews. Consider staff training on table turnover during peak hours.',
      improvements: [
        { metric: 'NPS Score', current: '72', target: '75', trend: 'improving', change: '+4' },
        { metric: 'Response Rate', current: '89%', target: '95%', trend: 'stable', change: '0%' }
      ],
      concerns: [],
      historicalCompletion: 91,
      avgCompletionTime: '18 mins',
      lastCompleted: '3 days ago'
    },
    'Supplier contract renewal': {
      impact: { revenue: 'N/A', margin: '+1.5%', rating: 'medium' },
      patterns: [
        { type: 'recurring', message: 'Annual renewal - 3 contracts due next month', trend: 'neutral' },
        { type: 'market', message: 'Market rates up 6% - negotiate volume discounts', trend: 'negative' },
        { type: 'positive', message: 'Current suppliers have 98% on-time delivery', trend: 'positive' }
      ],
      aiRecommendation: 'Lock in 12-month contracts now before predicted Q2 price increases. Consider consolidating produce suppliers for volume discount.',
      improvements: [
        { metric: 'COGS', current: '32%', target: '30%', trend: 'stable', change: '0%' },
        { metric: 'Delivery Reliability', current: '98%', target: '99%', trend: 'improving', change: '+1%' }
      ],
      concerns: [
        { area: 'Price Increase', issue: 'Seafood supplier requesting 8% increase', action: 'Get competing quotes' }
      ],
      historicalCompletion: 100,
      avgCompletionTime: '1.5 hours',
      lastCompleted: '12 months ago'
    }
  };
  
  // Return specific insights or generic ones
  return insights[task.title] || {
    impact: { revenue: 'TBD', margin: 'TBD', rating: task.priority },
    patterns: [
      { type: 'info', message: 'Analyzing historical patterns...', trend: 'neutral' }
    ],
    aiRecommendation: 'Complete this task to maintain operational efficiency and unlock detailed AI insights.',
    improvements: [],
    concerns: [],
    historicalCompletion: 80,
    avgCompletionTime: '30 mins',
    lastCompleted: 'N/A'
  };
};

// Enhanced Task Panel Component
export const EnhancedTaskPanel = ({ task, onClose, onComplete, onNavigate }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isCompleting, setIsCompleting] = useState(false);
  
  const insights = getTaskInsights(task);
  const navigation = TASK_NAVIGATION[task.category] || { path: '/app', section: 'Dashboard', icon: BarChart3 };
  const NavIcon = navigation.icon;
  
  const handleNavigateToArea = () => {
    onClose?.();
    const url = navigation.focus ? `${navigation.path}?focus=${navigation.focus}` : navigation.path;
    navigate(url);
  };
  
  const handleComplete = async () => {
    setIsCompleting(true);
    // Simulate completion
    await new Promise(r => setTimeout(r, 500));
    onComplete?.(task.id);
    setIsCompleting(false);
  };

  const priorityColors = {
    high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-500' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500' },
    low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', badge: 'bg-blue-500' }
  };
  
  const style = priorityColors[task.priority];

  return (
    <Portal><div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div 
        className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10">
          <div className="p-4 flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${style.badge} text-white text-xs`}>{task.priority} priority</Badge>
                <Badge variant="outline" className="text-xs capitalize">{task.category}</Badge>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Due: {task.due}
                <span className="mx-2">•</span>
                <History className="w-4 h-4" /> Avg time: {insights.avgCompletionTime}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Quick Action Bar */}
          <div className="px-4 pb-3 flex gap-2">
            <Button 
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={handleNavigateToArea}
            >
              <NavIcon className="w-4 h-4 mr-2" />
              Go to {navigation.section}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleComplete}
              disabled={isCompleting || task.status === 'completed'}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {task.status === 'completed' ? 'Completed' : isCompleting ? 'Completing...' : 'Mark Complete'}
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="p-4">
          <TabsList className="flex justify-start overflow-x-auto scrollbar-hide [&>*]:flex-shrink-0 md:grid md:grid-cols-4 mb-4">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="patterns" className="text-xs">Patterns</TabsTrigger>
            <TabsTrigger value="metrics" className="text-xs">Metrics</TabsTrigger>
            <TabsTrigger value="history" className="text-xs">History</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-0">
            {/* Impact Summary */}
            <Card className={`${style.bg} border ${style.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Impact Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
 <div className="grid grid-cols-1 gap-4">
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <DollarSign className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                    <p className="text-lg font-bold text-emerald-700">{insights.impact.revenue}</p>
                    <p className="text-xs text-muted-foreground">Revenue Impact</p>
                  </div>
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <TrendingUp className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                    <p className="text-lg font-bold text-blue-700">{insights.impact.margin}</p>
                    <p className="text-xs text-muted-foreground">Margin Impact</p>
                  </div>
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <Target className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                    <p className="text-lg font-bold text-purple-700 capitalize">{insights.impact.rating}</p>
                    <p className="text-xs text-muted-foreground">Priority</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* AI Recommendation */}
            <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4 text-violet-600" />
                  AI Recommendation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">{insights.aiRecommendation}</p>
              </CardContent>
            </Card>
            
            {/* Concerns */}
            {insights.concerns.length > 0 && (
              <Card className="bg-red-50/50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Areas Requiring Attention
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {insights.concerns.map((concern, idx) => (
                    <div key={`${concern?.id ?? concern?.name ?? concern?.title ?? 'concern'}-${idx}`} className="flex items-start gap-3 p-2 bg-white/60 rounded-lg">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{concern.area}</p>
                        <p className="text-xs text-gray-600">{concern.issue}</p>
                        <Badge variant="outline" className="mt-1 text-xs text-blue-600 border-blue-200">
                          {concern.action}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Patterns Tab */}
          <TabsContent value="patterns" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-blue-600" />
                  Pattern Recognition
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.patterns.map((pattern, idx) => (
                  <div 
                    key={`${pattern?.id ?? pattern?.name ?? pattern?.title ?? 'pattern'}-${idx}`} 
                    className={`p-3 rounded-lg border ${
                      pattern.trend === 'positive' ? 'bg-emerald-50 border-emerald-200' :
                      pattern.trend === 'negative' ? 'bg-red-50 border-red-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        pattern.trend === 'positive' ? 'bg-emerald-100' :
                        pattern.trend === 'negative' ? 'bg-red-100' :
                        'bg-slate-100'
                      }`}>
                        {pattern.trend === 'positive' ? <TrendingUp className="w-4 h-4 text-emerald-600" /> :
                         pattern.trend === 'negative' ? <TrendingDown className="w-4 h-4 text-red-600" /> :
                         <Activity className="w-4 h-4 text-slate-600" />}
                      </div>
                      <div>
                        <Badge variant="outline" className="text-xs capitalize mb-1">{pattern.type}</Badge>
                        <p className="text-sm text-gray-700">{pattern.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Historical Completion */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4 text-gray-600" />
                  Completion History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>On-time Completion Rate</span>
                      <span className="font-medium">{insights.historicalCompletion}%</span>
                    </div>
                    <Progress value={insights.historicalCompletion} className="h-2" />
                  </div>
 <div className="grid grid-cols-1 gap-4 pt-2">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold">{insights.avgCompletionTime}</p>
                      <p className="text-xs text-muted-foreground">Avg Completion Time</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold">{insights.lastCompleted}</p>
                      <p className="text-xs text-muted-foreground">Last Completed</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Metrics Tab */}
          <TabsContent value="metrics" className="space-y-4 mt-0">
            {insights.improvements.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    Related Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {insights.improvements.map((metric, idx) => (
                    <div key={`${metric?.id ?? metric?.name ?? metric?.title ?? 'metric'}-${idx}`} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{metric.metric}</span>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            metric.trend === 'improving' ? 'text-emerald-600 border-emerald-300' :
                            metric.trend === 'declining' ? 'text-red-600 border-red-300' :
                            'text-gray-600 border-gray-300'
                          }`}
                        >
                          {metric.trend === 'improving' ? <TrendingUp className="w-3 h-3 mr-1" /> :
                           metric.trend === 'declining' ? <TrendingDown className="w-3 h-3 mr-1" /> :
                           <Activity className="w-3 h-3 mr-1" />}
                          {metric.change}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Current</p>
                          <p className="text-lg font-bold">{metric.current}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-muted-foreground">Target</p>
                          <p className="text-lg font-bold text-primary">{metric.target}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-50">
                <CardContent className="py-8 text-center">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-muted-foreground">Complete this task to unlock detailed metrics</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  Recent Completions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { date: '7 days ago', time: '22 mins', user: 'You', status: 'on-time' },
                    { date: '14 days ago', time: '28 mins', user: 'You', status: 'on-time' },
                    { date: '21 days ago', time: '45 mins', user: 'You', status: 'delayed' },
                    { date: '28 days ago', time: '19 mins', user: 'You', status: 'on-time' },
                  ].map((entry, idx) => (
                    <div key={`${entry?.id ?? entry?.name ?? entry?.title ?? 'entry'}-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${entry.status === 'on-time' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span className="text-sm">{entry.date}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{entry.time}</span>
                        <span>{entry.user}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Structures to Minimize Risks */}
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Recommended Structures
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-start gap-2 p-2 bg-white/60 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Set recurring reminder</p>
                    <p className="text-xs text-muted-foreground">Automate weekly notification every Monday 9am</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/60 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Enable threshold alerts</p>
                    <p className="text-xs text-muted-foreground">Get notified when related metrics exceed targets</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white/60 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Delegate sub-tasks</p>
                    <p className="text-xs text-muted-foreground">Assign preparation work to team members</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div></Portal>
  );
};

// Enhanced Task Item for the list
export const EnhancedTaskItem = ({ task, onToggle, onExpand, compact = false }) => {
  const insights = getTaskInsights(task);
  const navigation = TASK_NAVIGATION[task.category] || { path: '/app', section: 'Dashboard', icon: BarChart3 };
  
  const priorityColors = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-slate-50 text-slate-600 border-slate-200'
  };
  
  return (
    <div 
      className={`p-3 rounded-xl transition-all cursor-pointer hover:shadow-md ${
        task.status === 'completed' ? 'bg-emerald-50/50 opacity-70' : 'bg-white/60 dark:bg-slate-800/60 hover:bg-white/80'
      } ${compact ? '' : 'border border-slate-200/50'}`}
      data-testid={`task-${task.id}`}
      onClick={() => onExpand?.(task)}
    >
      <div className="flex items-start gap-3">
        <Checkbox 
          checked={task.status === 'completed'}
          onCheckedChange={(e) => {
            e.stopPropagation();
            onToggle(task.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge variant="outline" className={`text-[10px] ${priorityColors[task.priority]}`}>
              {task.priority}
            </Badge>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {task.due}
            </span>
            {insights.impact.revenue !== 'TBD' && (
              <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
                <DollarSign className="w-3 h-3 mr-0.5" />
                {insights.impact.revenue}
              </Badge>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); onExpand?.(task); }}>
          <Eye className="w-3 h-3 mr-1" /> Details
        </Button>
      </div>
    </div>
  );
};

export default EnhancedTaskPanel;
